import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { IoCloseCircleSharp } from "react-icons/io5";
import useAlert from "../../../hook/useAlert";
import { debateFormatEnum, JudgeDebateReducerEnum } from "../../../utils/enum";
import { getOrdinal } from "../../../utils/methods";
import styles from "./DecisionBox.module.css";

function DecisionBox({
  debateType,
  teams,
  result,
  winTeamName,
  debateState,
  dispatch,
  JudgeDebate,
}) {
  const { open } = useAlert();
  const [isReadyToFinalize, setIsReadyToFinalize] = useState(false);

  useEffect(() => {
    if (debateState && debateType ) {
      if (
        debateType === debateFormatEnum["British Parliamentary"] &&
        Object.keys(result).length > 0
      ) {
        const isAllPositionsFilled = !Object.values(result).some(
          (val) => val === ""
        );

        setIsReadyToFinalize(isAllPositionsFilled && debateState.hasFinished);
      } else {
        setIsReadyToFinalize(debateState.hasFinished && winTeamName);
      }
    }
  }, [debateState, debateType, result, winTeamName]);

  const handleDrop = (position, teamName) => {
    if (debateType !== debateFormatEnum["British Parliamentary"]) return;

    if (isTeamAssigned(teamName)) {
      return open({ type: "error", text: "This team is already assigned." });
    }

    dispatch({
      type: JudgeDebateReducerEnum.UPDATE_COACH_DECISION_STATE,
      payload: {
        winnerTeamname: null,
        result: {
          ...result,
          [position]: teamName,
        },
      },
    });
  };

  const allowDrop = (ev) => {
    ev.preventDefault();
    ev.dataTransfer.getData("teamName");
  };

  const dragStart = (ev, teamName) => {
    ev.dataTransfer.setData("teamName", teamName);
  };

  const isTeamAssigned = (teamName) => {
    return Object.values(result).includes(teamName);
  };

  const removeTeam = (position) => {
    dispatch({
      type: JudgeDebateReducerEnum.UPDATE_COACH_DECISION_STATE,
      payload: {
        result: {
          ...result,
          [position]: "",
        },
      },
    });
  };

  /**
   * Finalizes the debate decision process.
   * This function should be called when the decision-making process is complete
   * and a winning team has been selected. It handles any final actions or state
   * updates needed to conclude the decision phase.
   */

  const handleFinishDecision = async () => {
    if (JudgeDebate.current) {
      await JudgeDebate.current.handleFinalizeCoachDecision();
      dispatch({
        type: JudgeDebateReducerEnum.UPDATE_COACH_DECISION_STATE,
        payload: {
          showResult: true,
        },
      });
    }
  };

  const handleSetWinTeamName = (teamName) => {
    if (debateType === debateFormatEnum["British Parliamentary"]) return;

    dispatch({
      type: JudgeDebateReducerEnum.UPDATE_COACH_DECISION_STATE,
      payload: {
        winnerTeamname: teamName,
        result: null,
      },
    });
  };

  if (!debateState) {
    return <>loading</>;
  }


  return (
    <div className={styles.DecisionBox}>
      <p className="text-[17px] text-slate-500">
        {debateType === debateFormatEnum["British Parliamentary"]
          ? "Please Drag and drop the team to their position."
          : "Please select the winner team."}
      </p>
      <div className={styles.team_box}>
        {teams.map((t) => (
          <div
            key={t.name} // Added key prop
            className={`${styles.team_box_item} ${
              winTeamName === t.name ? styles.selected_team_name : ""
            } flex items-center justify-center`}
            onDragStart={(e) => dragStart(e, t.name)}
            onClick={() => handleSetWinTeamName(t.name)}
          >
            <img
              width="44"
              height="44"
              src="https://img.icons8.com/3d-fluency/94/user-group-man-man--v4.png"
              alt="user-group-man-man--v4"
            />
            <p>{t.name}</p>
          </div>
        ))}
      </div>
      {debateType === debateFormatEnum["British Parliamentary"] && (
        <div className={styles.resultBox}>
          {result &&
            Object.keys(result).map((t) => (
              <div
                key={t} // Added key prop
                onDrop={(e) =>
                  handleDrop(Number(t), e.dataTransfer.getData("teamName"))
                }
                onDragOver={(e) => allowDrop(e)}
                className={styles.resultBoxItem}
              >
                {result[t] ? (
                  <>
                    <IoCloseCircleSharp
                      className={styles.removeIcon}
                      onClick={() => removeTeam(t)}
                    />
                    <img
                      width="40"
                      height="40"
                      src="https://img.icons8.com/3d-fluency/94/user-group-man-man--v4.png"
                      alt="user-group-man-man--v4"
                    />
                    <p>{result[t]}</p>
                  </>
                ) : (
                  <p>{getOrdinal(Number(t))}</p>
                )}
              </div>
            ))}
        </div>
      )}
      <button
        className={` ${styles.decisionButton} ${
          isReadyToFinalize ? styles.activeButton : ""
        }`}
        onClick={handleFinishDecision}
      >
        FINALIZE DECISION
      </button>
    </div>
  );
}

DecisionBox.propTypes = {
  debateType: PropTypes.string.isRequired,
  teams: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  result: PropTypes.shape({
    1: PropTypes.string.isRequired,
    2: PropTypes.string.isRequired,
    3: PropTypes.string.isRequired,
    4: PropTypes.string.isRequired,
  }),
  setResult: PropTypes.func.isRequired,
  RoomService: PropTypes.object.isRequired,
  winTeamName: PropTypes.string.isRequired,
  debateState: PropTypes.shape({
    hasFinished: PropTypes.bool.isRequired,
    isStarted: PropTypes.bool.isRequired,
  }),
  setWinTeamName: PropTypes.func.isRequired,
};

export default DecisionBox;
